<template>
<v-dialog v-model="isVisible" scrollable persistent fullscreen>
    <v-card>
        <v-card-text class="ma-0 pa-0">
            <v-row class="primary-header ma-0 pa-3" style="font-size: 25px" align="center">
                <span class="mr-3">요약보고서</span>
                <v-btn color="secondary" small @click="sponsor">개발자 후원하기</v-btn>
                <v-spacer />
                <v-btn color="green" small @click="copyToClipboard" :loading="copyLoading">이미지로 복사</v-btn>
                <v-btn color="green" small @click="downloadAbstract">다운로드</v-btn>
            </v-row>

            <v-row class="ma-0 py-3" justify="center" v-if="isLoading">
                <v-progress-circular size="24" indeterminate color="primary" />
            </v-row>

            <v-card class="ma-0 px-3">
                <div class="py-5" ref="report">
                    <return-table class="mb-5" v-model="abstracts" :colors="colors" @select="onReturnSelected" />

                    <v-card class="mb-5">
                        <v-card-title class="primary-header" style="font-size: 20px">월별 자산현황</v-card-title>
                        <v-switch class="ml-3" v-model="growthScale" hide-details label="로그스케일" />
                        <growth-chart class="py-3" :chart-data="growthData" :scale="growthScale" :height="400" />
                    </v-card>

                    <v-card class="mb-5">
                        <v-card-title class="primary-header" style="font-size: 20px">월별 낙폭 (drawdowns)</v-card-title>
                        <drawdown-chart class="py-3" :chart-data="drawdownData" :height="400" />
                    </v-card>

                    <v-card>
                        <v-card-title class="primary-header" style="font-size: 20px">연도별 수익률</v-card-title>
                        <return-chart class="py-3" :chart-data="returnData" :height="400" />
                    </v-card>
                </div>
            </v-card>

            <v-card class="my-8">
                <v-row class="primary-header ma-0 pa-2" style="font-size: 25px" align="center">
                    <span class="mr-3">연도별 자산 현황</span>
                    <v-spacer />
                    <v-btn color="secondary" @click="sponsor">개발자 후원하기</v-btn>
                    <v-btn color="green" @click="downloadDetails">다운로드</v-btn>
                </v-row>
                <v-col v-for="item in detailReports" :key="`detail-report-${item[0]}`" style="margin: auto">
                    <v-card class="mt-3 mb-3">
                        <v-card-title class="primary-header">
                            <span style="font-size: 20px">{{ item[0] }}</span>
                        </v-card-title>
                        <v-card-text>
                            <asset-detail-report v-model="item[1]" />
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-card>
        </v-card-text>

        <v-divider />

        <v-card-actions>
            <v-spacer />
            <v-btn color="red" @click="close">닫기</v-btn>
            <v-spacer />
        </v-card-actions>
    </v-card>

    <sponsor-dialog ref="sponsorDialog" />
</v-dialog>
</template>

<script>
import XLSX from 'xlsx'
import ReturnTable from '@/views/components/ReturnTable'
import GrowthChart from '@/views/charts/GrowthChart'
import ReturnChart from '@/views/charts/ReturnChart'
import DrawdownChart from '@/views/charts/DrawdownChart'
import AssetDetailReport from '@/views/components/AssetDetailReport'
import SponsorDialog from '@/views/dialogs/SponsorDialog'
import {
    IndexData
} from '@/constants'
import {
    Calculator
} from '@/mixins/calculator.js'

const randomColor = require('randomcolor')
const html2canvas = require('html2canvas')

export default {
    name: 'asset-chart-dialog',
    components: {
        ReturnTable,
        GrowthChart,
        ReturnChart,
        DrawdownChart,
        AssetDetailReport,
        SponsorDialog,
    },
    data() {
        return {
            isVisible: false,
            isLoading: false,
            copyLoading: false,
            docRef: '',
            colors: {
                '내 자산': '#f87979',
                'KOSPI': '#accbfc',
                'KOSDAQ': '#80eb34',
            },
            snapshots: [],
            abstracts: [],
            growthData: {},
            growthScale: false,
            returnData: {},
            drawdownData: {},
            detailReports: [],
        }
    },
    computed: {},
    methods: {
        async show(results, docRef) {
            this.isVisible = true
            this.results = results
            this.docRef = docRef

            this.isLoading = true
            this.snapshots = await docRef.collection('snapshots').orderBy('date').parseCollection()
            this.snapshots.push(results)
            this.isLoading = false

            const startCash = this.results.startCash
            const monthAxis = this.snapshots.map(x => {
                const splits = x.date.split('-')
                const year = parseInt(splits[0])
                const month = parseInt(splits[1])
                return `${year}-${month}`
            })
            const monthInterval = Calculator.getMonthInterval(
                this.results.startDate.split('-'),
                this.results.date.split('-'),
            )

            const monthlyPort = this.snapshots.map(x => {
                let stockAsset = 0
                for (const code in x.stocks) stockAsset += x.stocks[code].total * 0.9975
                return x.cash + stockAsset
            })

            const portMonthReturns = []
            for (let i = 1; i < monthAxis.length; i++) portMonthReturns.push(monthlyPort[i] / monthlyPort[i - 1] - 1)

            const monthlyKospi = IndexData.getIndexMonthly(startCash, monthAxis, 'kospi')
            const monthlyKosdaq = IndexData.getIndexMonthly(startCash, monthAxis, 'kosdaq')

            const kospiMonthReturns = IndexData.getIndexMonthlyReturns(monthAxis, 'kospi')
            const kosdaqMonthReturns = IndexData.getIndexMonthlyReturns(monthAxis, 'kosdaq')

            const portMonthDrawdowns = Calculator.getDrawdowns(portMonthReturns)
            const kospiMonthDrawdowns = Calculator.getDrawdowns(kospiMonthReturns)
            const kosdaqMonthDrawdowns = Calculator.getDrawdowns(kosdaqMonthReturns)

            const yearAxis = this.getYearAxis(monthAxis, 11)
            const portYearReturns = Calculator.buildYearReturns(monthlyPort)
            const kospiYearReturns = Calculator.buildYearReturns(monthlyKospi)
            const kosdaqYearReturns = Calculator.buildYearReturns(monthlyKosdaq)

            this.abstracts = [
                Calculator.buildAbstractResult('내 자산', startCash, monthInterval, monthlyPort, portYearReturns, portMonthDrawdowns),
                Calculator.buildAbstractResult('KOSPI', startCash, monthInterval, monthlyKospi, kospiYearReturns, kospiMonthDrawdowns),
                Calculator.buildAbstractResult('KOSDAQ', startCash, monthInterval, monthlyKosdaq, kosdaqYearReturns, kosdaqMonthDrawdowns)
            ]

            this.growthData = {
                labels: monthAxis,
                datasets: [{
                    label: '내 자산',
                    borderColor: '#f87979',
                    backgroundColor: '#f87979',
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    data: monthlyPort.map(x => x.toFixed(0))
                }, {
                    label: 'KOSPI',
                    borderColor: '#accbfc',
                    backgroundColor: '#accbfc',
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    data: monthlyKospi.map(x => x.toFixed(0))
                }, {
                    label: 'KOSDAQ',
                    borderColor: '#80eb34',
                    backgroundColor: '#80eb34',
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    data: monthlyKosdaq.map(x => x.toFixed(0))
                }]
            }

            this.drawdownData = {
                labels: monthAxis,
                datasets: [{
                    label: '내 자산',
                    borderColor: '#f87979',
                    backgroundColor: '#f87979',
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    data: portMonthDrawdowns
                }, {
                    label: 'KOSPI',
                    borderColor: '#accbfc',
                    backgroundColor: '#accbfc',
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    data: kospiMonthDrawdowns
                }, {
                    label: 'KOSDAQ',
                    borderColor: '#80eb34',
                    backgroundColor: '#80eb34',
                    fill: false,
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    data: kosdaqMonthDrawdowns
                }]
            }

            this.returnData = {
                labels: yearAxis,
                datasets: [{
                    label: '내 자산',
                    borderColor: '#f87979',
                    backgroundColor: '#f87979',
                    data: portYearReturns.map(x => x.toFixed(2))
                }, {
                    label: 'KOSPI',
                    borderColor: '#accbfc',
                    backgroundColor: '#accbfc',
                    data: kospiYearReturns.map(x => x.toFixed(2))
                }, {
                    label: 'KOSDAQ',
                    borderColor: '#80eb34',
                    backgroundColor: '#80eb34',
                    data: kosdaqYearReturns.map(x => x.toFixed(2))
                }]
            }

            this.buildDetailReports(yearAxis)
        },
        close() {
            this.snapshots = []
            this.isVisible = false
        },
        onReturnSelected(selected) {
            for (const graphItem of this.growthData.datasets) graphItem.hidden = !selected.map(x => x.name).includes(graphItem.label)
            for (const graphItem of this.returnData.datasets) graphItem.hidden = !selected.map(x => x.name).includes(graphItem.label)
            for (const graphItem of this.drawdownData.datasets) graphItem.hidden = !selected.map(x => x.name).includes(graphItem.label)

            this.growthData = Object.assign({}, this.growthData)
            this.returnData = Object.assign({}, this.returnData)
            this.drawdownData = Object.assign({}, this.drawdownData)
        },
        buildResult(name, startCash, interval, monthlyAmount, yearReturns, monthDrawdowns) {
            const cagr = 100 * (Math.pow(monthlyAmount[monthlyAmount.length - 1] / startCash, 1 / (interval[1] - interval[0])) - 1)
            const stdev = this.getStandardDeviation(yearReturns) * 100

            return {
                name,
                initial: startCash.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'),
                final: monthlyAmount[monthlyAmount.length - 1].toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'),
                cagr: `${cagr.toFixed(2)} %`,
                stdev: `${stdev.toFixed(2)} %`,
                best: `${Math.max.apply(null, yearReturns).toFixed(2)} %`,
                worst: `${Math.min.apply(null, yearReturns).toFixed(2)} %`,
                mdd: `${Math.min.apply(null, monthDrawdowns.filter(x => !isNaN(x))).toFixed(2)} %`,
                sharpe: (cagr / stdev).toFixed(2)
            }
        },
        buildDetailReports(yearAxis) {
            this.detailReports = []

            let yearIndex = 0
            for (let i = 0; i < yearAxis.length; i++) {
                const detailReport = {
                    labels: [],
                    datasets: []
                }

                const xAxis = []
                const cashs = []
                const allNameMap = {}
                for (let j = i * 12; j < Math.min(i * 12 + 12, this.snapshots.length) ; j++) {
                    xAxis.push(this.snapshots[j].date)
                    cashs.push(this.snapshots[j].cash)

                    for (const [code, value] of Object.entries(this.snapshots[j].stocks)) {
                        allNameMap[code] = value.name
                    }
                }

                const colors = randomColor({
                    count: Object.keys(allNameMap).length + 1
                })

                detailReport.labels = xAxis
                detailReport.datasets.push({
                    label: '현금 (000000)',
                    borderColor: colors[0],
                    backgroundColor: colors[0],
                    pointRadius: 2,
                    pointHoverRadius: 5,
                    fill: false,
                    hidden: false,
                    data: cashs
                })

                let index = 1
                for (const [code, name] of Object.entries(allNameMap)) {
                    const data = []
                    const rawData = this.snapshots.filter(v => v.stocks.hasOwnProperty(code))
                    for (const date of xAxis) {
                        const existingData = rawData.find(v => v.date == date)

                        if (existingData) data.push(existingData.stocks[code].total)
                        else data.push(null)
                    }

                    detailReport.datasets.push({
                        label: `${allNameMap[code]} (${code})`,
                        borderColor: colors[index],
                        backgroundColor: colors[index],
                        pointRadius: 2,
                        pointHoverRadius: 5,
                        fill: false,
                        hidden: false,
                        data
                    })
                    index++
                }

                this.detailReports.push([yearAxis[yearIndex], detailReport])
                yearIndex++
            }
        },
        downloadAbstract() {
            const wb = XLSX.utils.book_new()

            const abstractWS = XLSX.utils.json_to_sheet(this.abstracts)
            const growthWS = this.buildXlsxSheet(this.growthData)
            const drawdownWS = this.buildXlsxSheet(this.drawdownData)
            const returnWS = this.buildXlsxSheet(this.returnData)

            XLSX.utils.book_append_sheet(wb, abstractWS, '요약')
            XLSX.utils.book_append_sheet(wb, growthWS, '자산변동')
            XLSX.utils.book_append_sheet(wb, drawdownWS, 'MDD')
            XLSX.utils.book_append_sheet(wb, returnWS, '연도별 수익률')

            XLSX.writeFile(wb, '모의투자_요약보고서.xlsx')
        },
        downloadDetails() {
            const wb = XLSX.utils.book_new()
            for (const detail of this.detailReports) {
                const sheetName = detail[0]

                const items = []
                for (let i = 0; i < detail[1].labels.length; i++) {
                    const data = {}
                    data.date = detail[1].labels[i]
                    for (const dataset of detail[1].datasets) {
                        data[dataset.label] = dataset.data[i]
                    }
                    items.push(data)
                }
                
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(items, {header: ['date']}), sheetName)
            }
            
            XLSX.writeFile(wb, '모의투자_상세보고서.xlsx')
        },
        buildXlsxSheet(rawData) {
            const jsonData = []
            for (let i = 0; i < rawData.labels.length; i++) {
                const date = rawData.labels[i];
                const mine = rawData.datasets[0].data[i];
                const kospi = rawData.datasets[1].data[i];
                const kosdaq = rawData.datasets[2].data[i];
                jsonData.push({
                    date,
                    mine,
                    kospi,
                    kosdaq
                })
            }

            const ws = XLSX.utils.json_to_sheet(jsonData)
            for (const key in ws) {
                if (key.startsWith('!') || key.substring(1) == '1') continue

                if (!key.startsWith('A')) {
                    ws[key]['t'] = 'n'
                }
            }

            return ws
        },
        getYearAxis(monthAxis, offset) {
            const yearAxis = []
            for (let i = 0; i < monthAxis.length; i++) {
                if (i % 12 == 0) {
                    if (i + 12 > monthAxis.length - 1) {
                        yearAxis.push(`${monthAxis[i]} ~ ${monthAxis[monthAxis.length - 1]}`)
                    } else {
                        yearAxis.push(`${monthAxis[i]} ~ ${monthAxis[i + offset]}`)
                    }
                }
            }
            return yearAxis
        },
        copyToClipboard() {
            this.copyLoading = true

            html2canvas(this.$refs.report, {
                    scrollX: 0,
                    scrollY: -window.scrollY + 100,
                }).then(canvas => {
                    canvas.toBlob(blob => navigator.clipboard.write([new ClipboardItem({
                        'image/png': blob
                    })]))
                    this.$toasted.success('클립보드에 복사됐습니다')
                }).catch(e => {
                    this.$toasted.error('이미지 복사 중에 오류가 발생했습니다')
                })
                .finally(() => this.copyLoading = false)
        },
        sponsor() {
            if (this.$store.state.mobile) window.open('https://qr.kakaopay.com/2810060111039909710072079c402115')
            else this.$refs.sponsorDialog.show()
        }
    }
}
</script>
